import { num } from "@/lib/format";
import { Tips, Stat } from "@/components/marketing/MarketingTips";
import { ExternalLink } from "lucide-react";

const Bars = ({ title, rows, label, testid }) => {
  const max = Math.max(...rows.map((r) => r.median), 1);
  return (
    <div data-testid={testid}>
      <p className="mb-2 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{title}</p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={label(r)} className="flex items-center gap-2 text-xs">
            <span className="w-20 shrink-0">{label(r)}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary" style={{ width: `${(r.median / max) * 100}%` }} /></div>
            <span className="w-24 shrink-0 text-right text-muted-foreground">{num(r.median)} · {r.count} post</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PostList = ({ title, posts, unit, testid }) => (
  <div data-testid={testid}>
    <p className="mb-2 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{title}</p>
    <div className="space-y-2">
      {posts.map((p, i) => (
        <a key={p.permalink || `${p.date}-${p.caption}`} href={p.permalink} target="_blank" rel="noreferrer" data-testid={`${testid}-${i}`}
          className="flex items-start justify-between gap-3 rounded-md border border-border/60 p-2 text-xs transition-colors hover:border-primary/40">
          <span className="min-w-0"><span className="text-muted-foreground">{p.date.split("-").reverse().join("/")} · {p.kind}</span><span className="block truncate">{p.caption || "(senza testo)"}</span></span>
          <span className="flex shrink-0 items-center gap-1 font-semibold">{num(p.value)} {unit}<ExternalLink className="h-3 w-3 text-muted-foreground" /></span>
        </a>
      ))}
    </div>
  </div>
);

const Platform = ({ name, s, unit, testid }) => (
  <div className="space-y-4 rounded-lg border border-border/60 p-4" data-testid={testid}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h4 className="font-display font-semibold">{name}</h4>
      <span className="text-xs text-muted-foreground">{s.posts_per_week} post/settimana · mediana {num(s.median)} {unit}</span>
    </div>
    <Bars title={`Per formato (${unit} mediane)`} rows={s.by_kind} label={(r) => r.kind} testid={`${testid}-kinds`} />
    {s.weekdays.length > 0 && <Bars title="Per giorno di pubblicazione" rows={s.weekdays} label={(r) => r.day} testid={`${testid}-days`} />}
    <PostList title="Contenuti migliori" posts={s.top} unit={unit} testid={`${testid}-top`} />
    {s.flop.length > 0 && <PostList title="Contenuti più deboli" posts={s.flop} unit={unit} testid={`${testid}-flop`} />}
  </div>
);

export const MarketingProfiles = ({ p }) => (
  <div className="space-y-5" data-testid="marketing-profiles">
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Stat label="Follower Instagram" value={num(p.followers)} testid="marketing-ig-followers" />
      <Stat label="Follower Facebook" value={num(p.fb_followers)} testid="marketing-fb-followers" />
      <Stat label="Pubblico donne" value={p.audience.female_pct != null ? `${p.audience.female_pct}%` : "–"} testid="marketing-female" />
      <Stat label="Età principali" value={p.audience.top_ages.join(" · ") || "–"} testid="marketing-ages" />
    </div>
    <Tips tips={p.tips} testid="marketing-profile-tips" />
    {p.audience.top_cities.length > 0 && <p className="text-xs text-muted-foreground">Città principali: {p.audience.top_cities.join(", ")}</p>}
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {p.ig && <Platform name="Instagram · ultimi 6 mesi" s={p.ig} unit="persone" testid="marketing-ig" />}
      {p.fb && <Platform name="Facebook · ultimi 12 mesi" s={p.fb} unit="visual." testid="marketing-fb" />}
    </div>
  </div>
);
